import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { accessQuery } from '../api/queries';

/**
 * Permission keys the web uses to HIDE actions the user cannot perform. Hiding is a courtesy:
 * the server enforces every permission on every request.
 */
export type Permission =
  | 'organization.view'
  | 'settings.manage'
  | 'audit.view'
  | 'customer.view'
  | 'customer.create'
  | 'customer.edit'
  | 'item.view'
  | 'item.create'
  | 'item.edit'
  | 'invoice.view'
  | 'invoice.create'
  | 'invoice.edit'
  | 'invoice.issue'
  | 'invoice.submit'
  | 'invoice.cancel'
  | 'invoice.decline'
  | 'supplier.view'
  | 'receipt.view'
  | 'receipt.create'
  | 'receipt.submit'
  | 'receipt.return'
  | 'purchase.view'
  | 'payment.view'
  | 'report.sales.view'
  | 'report.purchases.view'
  | 'report.vat.view'
  | 'report.vat.export'
  | 'form41.view'
  | 'form41.export'
  | 'users.view'
  | 'import.run';

export interface AccessInfo {
  role: string | undefined;
  can: (permission: Permission) => boolean;
  isLoaded: boolean;
}

export function useAccess(orgId: string | undefined): AccessInfo {
  const { data } = useQuery({ ...accessQuery(orgId ?? ''), enabled: Boolean(orgId) });
  return useMemo(() => {
    const granted = new Set(data?.permissions ?? []);
    return {
      role: data?.role,
      can: (permission: Permission) => granted.has(permission),
      isLoaded: data !== undefined,
    };
  }, [data]);
}
